/* eslint-disable unused-imports/no-unused-vars */
import { Dispatch, FormEvent, SetStateAction, useCallback, useEffect, useState } from "react";

import { InteractionObjectType, InteractionType, ProjectIssuanceTransactionConstants } from "../../../../constants";
import { PillData, ProjectIssuanceTransactionPanelData } from "../../../../models";
import { updateIssuanceTransaction } from "../../../../service/project";
import { searchInteractions, SearchIssuanceTransactionsResponse } from "../../../../service/query";
import { ServiceError, Status } from "../../../../service/Shared";
import { useAuth } from "../../../../useAuth";
import { useIsLoadingWrapper } from "../../../../utils";
import { dataGridMapFilterCriteria, Toast } from "../../../../widget";
import { InteractionTag } from "../../../TagsPanel/models";

interface UseEditIssuanceTransactionPanelProps {
  issuanceTransaction: SearchIssuanceTransactionsResponse["results"][0];
  onClose: () => void;
  handleRefresh: () => void;
}

interface UseEditIssuanceTransactionPanelReturnData {
  issuanceTransactionPanelData: ProjectIssuanceTransactionPanelData;
  setIssuanceTransactionPanelData: Dispatch<SetStateAction<ProjectIssuanceTransactionPanelData>>;
  errors: ServiceError[] | undefined;
  isHandleSubmitLoading: boolean;
  handleClosePanel: () => void;
  handleResetPanel: () => void;
  handleSubmit: (e: FormEvent<HTMLFormElement>) => Promise<void>;

  showTagsPanel: boolean;
  setShowTagsPanel: Dispatch<SetStateAction<boolean>>;
  tagPills: PillData[];
  setRefreshTags: Dispatch<SetStateAction<boolean>>;
}

export const useEditIssuanceTransactionPanel = ({
  issuanceTransaction,
  onClose,
  handleRefresh,
}: UseEditIssuanceTransactionPanelProps): UseEditIssuanceTransactionPanelReturnData => {
  const { currentOrganisationUuid } = useAuth();

  const [errors, setErrors] = useState<ServiceError[] | undefined>();
  const [isHandleSubmitLoading, setIsHandleSubmitLoading] = useState(false);
  const [showTagsPanel, setShowTagsPanel] = useState(false);
  const [tagPills, setTagPills] = useState<PillData[]>([]);
  const [refreshTags, setRefreshTags] = useState(false);

  // eslint-disable-next-line no-undef
  const [issuanceTransactionPanelData, setIssuanceTransactionPanelData] = useState<ProjectIssuanceTransactionPanelData>(
    {
      ...ProjectIssuanceTransactionConstants.DEFAULT_ISSUANCE_TRANSACTION,
      issuanceUuid: issuanceTransaction.issuanceUuid,
      parentTransactionUuid: issuanceTransaction.uuid,
      counterParty: issuanceTransaction.counterParty,
      quantity: issuanceTransaction.quantity,
      price: issuanceTransaction.price,
      fees: issuanceTransaction.fees,
      totalValue: issuanceTransaction.totalValue,
      areaHa: issuanceTransaction.areaHa,
      externalReference: issuanceTransaction.externalReference,
      rowVersion: issuanceTransaction.rowVersion,
      transactionDate: issuanceTransaction.transactionDate,
      uuid: issuanceTransaction.uuid,
      type: issuanceTransaction.type,
      broker: issuanceTransaction.broker,
    }
  );

  const handleResetPanel = (): void => {
    setIssuanceTransactionPanelData({
      ...ProjectIssuanceTransactionConstants.DEFAULT_ISSUANCE_TRANSACTION,
      issuanceUuid: issuanceTransaction.issuanceUuid,
      parentTransactionUuid: issuanceTransaction.uuid,
      counterParty: issuanceTransaction.counterParty,
      quantity: issuanceTransaction.quantity,
      price: issuanceTransaction.price,
      fees: issuanceTransaction.fees,
      totalValue: issuanceTransaction.totalValue,
      areaHa: issuanceTransaction.areaHa,
      externalReference: issuanceTransaction.externalReference,
      rowVersion: issuanceTransaction.rowVersion,
      transactionDate: issuanceTransaction.transactionDate,
      uuid: issuanceTransaction.uuid,
      type: issuanceTransaction.type,
      broker: issuanceTransaction.broker,
    });
    setErrors([]);
  };

  const handleClosePanel = (): void => {
    onClose();
    setIssuanceTransactionPanelData({
      ...ProjectIssuanceTransactionConstants.DEFAULT_ISSUANCE_TRANSACTION,
    });
    setShowTagsPanel(false);
    setTagPills([]);
    setErrors([]);
  };
  const handleSubmit = useIsLoadingWrapper(async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    setErrors([]);

    const res = await updateIssuanceTransaction({
      areaHa: issuanceTransactionPanelData.areaHa,
      counterParty: issuanceTransactionPanelData.counterParty ?? "",
      externalReference: issuanceTransactionPanelData.externalReference,
      fees: issuanceTransactionPanelData.fees,
      price: issuanceTransactionPanelData.price,
      quantity: issuanceTransactionPanelData.quantity ?? 0,
      broker: issuanceTransactionPanelData.broker ?? "",
      totalValue:
        issuanceTransactionPanelData.totalValue !== null
          ? parseFloat(issuanceTransactionPanelData.totalValue.toFixed(5))
          : null,
      transactionDate: issuanceTransactionPanelData.transactionDate,
      uuid: issuanceTransactionPanelData.uuid ?? "",
      rowVersion: issuanceTransactionPanelData.rowVersion ?? 0,
    });

    if (res.status === Status.Success) {
      Toast.success({ message: "Issuance transaction updated successfully" });
      handleRefresh();
      handleClosePanel();
    }

    if (res.status === Status.Error) {
      setErrors(res.errors);
    }
  }, setIsHandleSubmitLoading);

  const fetchTags = useCallback(
    async (uuid: string, orgUuid: string): Promise<void> => {
      const filterCriteria = dataGridMapFilterCriteria([]);
      filterCriteria.type = {
        operator: "eq",
        value: InteractionType.TAG,
      };
      filterCriteria.objectType = {
        operator: "eq",
        value: InteractionObjectType.ORGANISATION,
      };
      filterCriteria.objectUuid = {
        operator: "eq",
        value: orgUuid,
      };
      filterCriteria.objectInteractions = {
        some: {
          objectUuid: {
            operator: "eq",
            value: uuid,
          },
          endDate: {
            operator: "eq",
            value: null,
          },
        },
      };
      const response = await searchInteractions({
        paging: {
          beforeCursor: null,
          afterCursor: null,
          limit: 50,
        },
        filter: { results: filterCriteria },
      });

      if (response.data?.results != null) {
        const existingTags =
          response.data.results.map(
            (x) =>
              ({
                uuid: x.uuid,
                data: JSON.parse(x.data),
                checked: true,
                rowVersion: x.rowVersion,
              }) as InteractionTag
          ) ?? [];
        setTagPills(existingTags.map((tag) => ({ key: tag.uuid, label: tag.data.name })));
      }
    },
    [issuanceTransaction.uuid, currentOrganisationUuid, refreshTags]
  );

  useEffect(() => {
    handleRefresh();
  }, [issuanceTransaction.uuid]);

  useEffect(() => {
    if (currentOrganisationUuid) {
      fetchTags(issuanceTransaction.uuid, currentOrganisationUuid);
    }
  }, [issuanceTransaction.uuid, currentOrganisationUuid, refreshTags]);

  return {
    errors,
    isHandleSubmitLoading,
    issuanceTransactionPanelData,
    setIssuanceTransactionPanelData,
    handleClosePanel,
    handleResetPanel,
    handleSubmit,
    showTagsPanel,
    setShowTagsPanel,
    tagPills,
    setRefreshTags,
  };
};
